import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import propertyService from '../../services/propertyService';
import { supabase } from '../../lib/supabase';

/**
 * Parcours d'achat Québec - Timeline avec checklists
 * Adapté du style V2 vers le style V1
 */
const PurchaseJourney = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStep, setExpandedStep] = useState(null);
  const [completedItems, setCompletedItems] = useState({});
  const [saving, setSaving] = useState(false);

  const propertyId = searchParams.get('propertyId');

  // Load property data
  useEffect(() => {
    const loadProperty = async () => {
      if (!propertyId || !user?.id) {
        setLoading(false);
        return;
      }

      try {
        const result = await propertyService.getById(propertyId);
        if (result?.success) {
          setProperty(result.data);
          // Load saved progress
          if (result.data?.purchase_checklist) {
            setCompletedItems(result.data.purchase_checklist);
          }
          // Auto-expand first incomplete step
          const firstIncomplete = steps.findIndex(step => 
            !isStepComplete(step.id, result.data?.purchase_checklist || {})
          );
          setExpandedStep(firstIncomplete >= 0 ? steps[firstIncomplete].id : null);
        }
      } catch (err) {
        console.error('Error loading property:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [propertyId, user]);

  const steps = [
    {
      id: 'validation',
      title: 'Validation du deal',
      icon: 'CheckCircle',
      description: 'Confirmer que les chiffres tiennent la route',
      items: [
        { id: 'verify_revenues', label: 'Vérifier les revenus déclarés vs réels' },
        { id: 'validate_expenses', label: 'Valider les dépenses (taxes, assurances, entretien)' },
        { id: 'confirm_financing', label: 'Confirmer la faisabilité du financement' },
        { id: 'check_market', label: 'Comparer avec le marché local' }
      ],
      risk: 'Risque si ignoré: acheter un immeuble non rentable ou surévalué',
      documents: []
    },
    {
      id: 'offer',
      title: 'Offre acceptée',
      icon: 'FileText',
      description: 'Négociation et signature de la promesse d\'achat',
      items: [
        { id: 'draft_offer', label: 'Rédiger la promesse d\'achat avec conditions' },
        { id: 'negotiate_conditions', label: 'Négocier le prix et les conditions' },
        { id: 'sign_offer', label: 'Signer la promesse d\'achat' },
        { id: 'seller_acceptance', label: 'Obtenir l\'acceptation du vendeur' }
      ],
      risk: 'Risque si ignoré: perdre la propriété ou payer trop cher',
      documents: ['Promesse d\'achat signée', 'Contre-offres']
    },
    {
      id: 'inspection',
      title: 'Inspection',
      icon: 'Search',
      description: 'Vérification de l\'état physique du bâtiment',
      items: [
        { id: 'book_inspector', label: 'Réserver un inspecteur en bâtiment certifié' },
        { id: 'full_inspection', label: 'Inspection pré-achat complète' },
        { id: 'roof_check', label: 'Vérification de la toiture et isolation' },
        { id: 'plumbing_electric', label: 'Inspection plomberie et électricité' },
        { id: 'foundation', label: 'Évaluation de la fondation' },
        { id: 'review_report', label: 'Analyser le rapport et négocier si nécessaire' }
      ],
      risk: 'Risque si ignoré: découvrir des vices cachés coûteux après l\'achat',
      documents: ['Rapport d\'inspection', 'Photos des défauts', 'Estimés de réparation']
    },
    {
      id: 'tenants',
      title: 'Baux & locataires',
      icon: 'Users',
      description: 'Analyser les baux et valider les locataires',
      items: [
        { id: 'obtain_leases', label: 'Obtenir tous les baux signés' },
        { id: 'verify_rents', label: 'Vérifier que les loyers correspondent' },
        { id: 'renewal_dates', label: 'Noter les dates de renouvellement' },
        { id: 'payment_history', label: 'Demander l\'historique de paiement' },
        { id: 'meet_tenants', label: 'Rencontrer les locataires si possible' }
      ],
      risk: 'Risque si ignoré: locataires problématiques ou loyers surévalués',
      documents: ['Copies des baux', 'Historique de paiement', 'Coordonnées locataires']
    },
    {
      id: 'legal',
      title: 'Légal & municipal',
      icon: 'Scale',
      description: 'Vérifications légales et conformité municipale',
      items: [
        { id: 'location_cert', label: 'Obtenir un certificat de localisation à jour' },
        { id: 'zoning', label: 'Vérifier le zonage et les droits acquis' },
        { id: 'taxes_current', label: 'Confirmer que les taxes municipales sont à jour' },
        { id: 'rental_permits', label: 'Vérifier les permis de location requis' },
        { id: 'liens_check', label: 'Vérifier les hypothèques et charges sur le titre' }
      ],
      risk: 'Risque si ignoré: problèmes légaux, amendes, ou titre contesté',
      documents: ['Certificat de localisation', 'Compte de taxes', 'Recherche de titres']
    },
    {
      id: 'financing',
      title: 'Financement',
      icon: 'Landmark',
      description: 'Obtenir l\'approbation finale du prêt hypothécaire',
      items: [
        { id: 'preapproval', label: 'Obtenir la pré-approbation hypothécaire' },
        { id: 'submit_docs', label: 'Soumettre le dossier complet au prêteur' },
        { id: 'appraisal', label: 'Évaluation par l\'institution financière' },
        { id: 'firm_commitment', label: 'Obtenir l\'engagement ferme du prêteur' },
        { id: 'confirm_terms', label: 'Confirmer les conditions finales du prêt' }
      ],
      risk: 'Risque si ignoré: financement refusé à la dernière minute',
      documents: ['Lettre d\'engagement', 'Conditions du prêt', 'Rapport d\'évaluation']
    },
    {
      id: 'notary',
      title: 'Notaire',
      icon: 'FileSignature',
      description: 'Préparation et signature de l\'acte de vente',
      items: [
        { id: 'choose_notary', label: 'Choisir un notaire immobilier' },
        { id: 'review_deed', label: 'Réviser l\'acte de vente' },
        { id: 'prepare_funds', label: 'Préparer les fonds pour la clôture' },
        { id: 'review_adjustments', label: 'Vérifier l\'état des ajustements' }
      ],
      risk: 'Risque si ignoré: erreurs dans l\'acte ou fonds manquants',
      documents: ['Acte de vente', 'État des ajustements', 'Bordereau de clôture']
    },
    {
      id: 'possession',
      title: 'Prise de possession',
      icon: 'Key',
      description: 'Finalisation de l\'achat et transfert',
      items: [
        { id: 'sign_deed', label: 'Signature chez le notaire' },
        { id: 'transfer_funds', label: 'Transfert des fonds au vendeur' },
        { id: 'receive_keys', label: 'Réception des clés' },
        { id: 'meet_tenants_final', label: 'Rencontre avec les locataires pour le transfert' },
        { id: 'change_insurances', label: 'Transférer les assurances' },
        { id: 'notify_city', label: 'Notifier la ville du changement de propriétaire' }
      ],
      risk: 'Risque si ignoré: transition chaotique avec les locataires',
      documents: ['Clés et codes d\'accès', 'Manuels d\'équipements', 'Coordonnées fournisseurs']
    }
  ];

  const isStepComplete = (stepId, items = completedItems) => {
    const step = steps.find(s => s.id === stepId);
    if (!step) return false;
    return step.items.every(item => items[`${stepId}_${item.id}`]);
  };

  const getStepProgress = (stepId) => {
    const step = steps.find(s => s.id === stepId);
    if (!step) return 0;
    const completed = step.items.filter(item => completedItems[`${stepId}_${item.id}`]).length;
    return Math.round((completed / step.items.length) * 100);
  };

  const toggleItem = async (stepId, itemId) => {
    const key = `${stepId}_${itemId}`;
    const newValue = !completedItems[key];
    
    setCompletedItems(prev => ({
      ...prev,
      [key]: newValue
    }));

    // Save to database
    if (propertyId) {
      setSaving(true);
      try {
        const updatedChecklist = {
          ...completedItems,
          [key]: newValue
        };
        
        await supabase
          .from('properties')
          .update({ purchase_checklist: updatedChecklist })
          .eq('id', propertyId);
      } catch (err) {
        console.error('Error saving checklist:', err);
      } finally {
        setSaving(false);
      }
    }
  };

  const totalProgress = () => {
    const totalItems = steps.reduce((acc, step) => acc + step.items.length, 0);
    const completedCount = Object.values(completedItems).filter(Boolean).length;
    return Math.round((completedCount / totalItems) * 100);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-background)' }}>
        <NavigationHeader />
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          paddingTop: '64px'
        }}>
          <Icon name="Loader2" size={32} className="animate-spin" color="var(--color-primary)" />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-background)' }}>
      <NavigationHeader />
      
      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: 'calc(64px + var(--spacing-xl)) var(--spacing-lg) var(--spacing-xl)'
      }}>
        {/* Header */}
        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
            iconPosition="left"
            onClick={() => navigate(propertyId ? `/analysis-results?propertyId=${propertyId}` : '/main-dashboard')}
            style={{ marginBottom: 'var(--spacing-md)' }}
          >
            Retour
          </Button>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 'var(--spacing-md)'
          }}>
            <div>
              <h1 style={{
                fontSize: '1.75rem',
                fontWeight: 700,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Parcours d'achat
              </h1>
              <p style={{ color: 'var(--color-muted-foreground)' }}>
                {property?.address || 'Guide d\'achat immobilier Québec'}
              </p>
            </div>
            
            {saving && (
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)',
                fontSize: '0.75rem',
                color: 'var(--color-muted-foreground)'
              }}>
                <Icon name="Loader2" size={14} className="animate-spin" />
                Sauvegarde...
              </span>
            )}
          </div>
        </div>

        {/* Progress Overview */}
        <div style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-lg)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-md)'
          }}>
            <h2 style={{
              fontSize: '1rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-sm)'
            }}>
              <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
              Progression globale
            </h2>
            <span style={{
              background: 'var(--color-primary)',
              color: 'white',
              padding: 'var(--spacing-xs) var(--spacing-sm)',
              borderRadius: 'var(--radius-full)',
              fontSize: '0.875rem',
              fontWeight: 600
            }}>
              {totalProgress()}%
            </span>
          </div>
          
          <div style={{
            height: '10px',
            background: 'var(--color-muted)',
            borderRadius: 'var(--radius-full)',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${totalProgress()}%`,
              height: '100%',
              background: 'var(--color-primary)',
              transition: 'width 0.3s ease',
              borderRadius: 'var(--radius-full)'
            }} />
          </div>
          
          <p style={{
            fontSize: '0.875rem',
            color: 'var(--color-muted-foreground)',
            marginTop: 'var(--spacing-sm)'
          }}>
            {steps.filter(s => isStepComplete(s.id)).length} sur {steps.length} étapes complétées
          </p>
        </div>

        {/* Steps Timeline */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
          {steps.map((step, index) => {
            const isComplete = isStepComplete(step.id);
            const progress = getStepProgress(step.id);
            const isExpanded = expandedStep === step.id;

            return (
              <div
                key={step.id}
                style={{
                  background: 'var(--color-surface)',
                  border: `2px solid ${isComplete ? 'var(--color-success)' : isExpanded ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-lg)',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Step Header */}
                <button
                  onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-lg)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-md)',
                    textAlign: 'left'
                  }}
                >
                  {/* Step number/status */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: 'var(--radius-md)',
                    background: isComplete 
                      ? 'var(--color-success)' 
                      : progress > 0
                        ? 'var(--color-primary)'
                        : 'var(--color-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {isComplete ? (
                      <Icon name="Check" size={24} color="white" />
                    ) : (
                      <span style={{
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        color: progress > 0 ? 'white' : 'var(--color-muted-foreground)'
                      }}>
                        {index + 1}
                      </span>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-sm)',
                      marginBottom: 'var(--spacing-xs)'
                    }}>
                      <h3 style={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'var(--color-foreground)'
                      }}>
                        {step.title}
                      </h3>
                      {progress > 0 && !isComplete && (
                        <span style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-primary)',
                          fontWeight: 500
                        }}>
                          {progress}%
                        </span>
                      )}
                    </div>
                    <p style={{
                      fontSize: '0.875rem',
                      color: 'var(--color-muted-foreground)'
                    }}>
                      {step.description}
                    </p>
                  </div>

                  <Icon
                    name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                    size={20}
                    color="var(--color-muted-foreground)"
                  />
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div style={{
                    padding: '0 var(--spacing-lg) var(--spacing-lg)',
                    borderTop: '1px solid var(--color-border)'
                  }}>
                    {/* Checklist */}
                    <div style={{ marginTop: 'var(--spacing-md)' }}>
                      <p style={{
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--color-muted-foreground)',
                        marginBottom: 'var(--spacing-sm)',
                        fontWeight: 600
                      }}>
                        Checklist
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                        {step.items.map((item) => {
                          const isChecked = completedItems[`${step.id}_${item.id}`];
                          return (
                            <button
                              key={item.id}
                              onClick={() => toggleItem(step.id, item.id)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-md)',
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                background: isChecked ? 'rgba(34, 197, 94, 0.1)' : 'var(--color-muted)',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <div style={{
                                width: '22px',
                                height: '22px',
                                borderRadius: 'var(--radius-sm)',
                                border: isChecked ? 'none' : '2px solid var(--color-border)',
                                background: isChecked ? 'var(--color-success)' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}>
                                {isChecked && <Icon name="Check" size={14} color="white" />}
                              </div>
                              <span style={{
                                fontSize: '0.875rem',
                                color: isChecked ? 'var(--color-muted-foreground)' : 'var(--color-foreground)',
                                textDecoration: isChecked ? 'line-through' : 'none'
                              }}>
                                {item.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Documents */}
                    {step.documents && step.documents.length > 0 && (
                      <div style={{ marginTop: 'var(--spacing-lg)' }}>
                        <p style={{
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: 'var(--color-muted-foreground)',
                          marginBottom: 'var(--spacing-sm)',
                          fontWeight: 600
                        }}>
                          Documents à obtenir
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-xs)' }}>
                          {step.documents.map((doc, i) => (
                            <span
                              key={i}
                              style={{
                                padding: 'var(--spacing-xs) var(--spacing-sm)',
                                background: 'var(--color-muted)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: '0.75rem',
                                color: 'var(--color-foreground)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-xs)'
                              }}
                            >
                              <Icon name="FileText" size={12} color="var(--color-primary)" />
                              {doc}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Risk Warning */}
                    {step.risk && (
                      <div style={{
                        marginTop: 'var(--spacing-lg)',
                        padding: 'var(--spacing-md)',
                        background: 'rgba(245, 158, 11, 0.1)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: 'var(--radius-md)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'var(--spacing-sm)'
                      }}>
                        <Icon name="AlertTriangle" size={18} color="#f59e0b" style={{ marginTop: '2px' }} />
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#92400e'
                        }}>
                          {step.risk}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Complete CTA */}
        {totalProgress() === 100 && (
          <div style={{
            marginTop: 'var(--spacing-xl)',
            padding: 'var(--spacing-xl)',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '2px solid var(--color-success)',
            borderRadius: 'var(--radius-lg)',
            textAlign: 'center'
          }}>
            <Icon name="PartyPopper" size={48} color="var(--color-success)" style={{ marginBottom: 'var(--spacing-md)' }} />
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 700,
              marginBottom: 'var(--spacing-sm)',
              color: 'var(--color-foreground)'
            }}>
              Félicitations!
            </h3>
            <p style={{
              color: 'var(--color-muted-foreground)',
              marginBottom: 'var(--spacing-lg)'
            }}>
              Vous avez complété toutes les étapes du parcours d'achat.
            </p>
            <Button
              variant="primary"
              size="lg"
              iconName="Building"
              iconPosition="left"
              onClick={() => navigate('/property-planning')}
            >
              Passer à la gestion de propriété
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default PurchaseJourney;
